import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import MainLayout from './layout/MainLayout';
import Dashboard from './pages/Dashboard';
import Streams from './pages/Streams';
import TopStreamers from './pages/TopStreamers';
import Community from './pages/Community';
import FAQ from './pages/FAQ';
import FAQDetail from './pages/FAQDetail';
import Information from './pages/Information';
import StreamerAccount from './pages/StreamerAccount';
import RedeemedCoins from './pages/RedeemedCoins';
import Giveaway from './pages/Giveaway';
import TwitchAccount from './pages/TwitchAccount';
import TwitchTimers from './pages/TwitchTimers';
import TwitchCommands from './pages/TwitchCommands';
import ChatPanel from './components/ChatPanel';

const CREATOR_PLUS_ROLE_ID = '1492514375905316940';

function ChatPage() {
  const [hasCreatorPlus, setHasCreatorPlus] = useState(false);
  const isAuthenticated = !!localStorage.getItem('discord_token');

  useEffect(() => {
    if (!isAuthenticated) {
      setHasCreatorPlus(false);
      return;
    }

    let cancelled = false;
    const token = localStorage.getItem('discord_token');

    fetch(`${process.env.REACT_APP_API_BASE || '/api'}/user-data`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => {
        if (!cancelled) {
          setHasCreatorPlus(Array.isArray(data.roles) && data.roles.includes(CREATOR_PLUS_ROLE_ID));
        }
      })
      .catch(() => {
        if (!cancelled) setHasCreatorPlus(false);
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  return <ChatPanel isAuthenticated={isAuthenticated} hasCreatorPlus={hasCreatorPlus} variant="page" />;
}

const API_BASE = process.env.REACT_APP_API_BASE;

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('discord_token'));
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');

    if (token) {
      localStorage.setItem('discord_token', token);
      window.history.replaceState({}, document.title, window.location.pathname);
      window.location.reload();
      return;
    }

    const validateToken = async () => {
      const token = localStorage.getItem('discord_token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE}/api/user-data`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserData(response.data);
        setIsAuthenticated(true);
        setApprovalStatus(null);
      } catch (error) {
        console.error('Token validation failed:', error);
        if (error.response?.data?.error === 'pending_approval') {
          setApprovalStatus('pending');
          setIsAuthenticated(false);
        } else if (error.response?.data?.error === 'access_denied') {
          setApprovalStatus('rejected');
          setIsAuthenticated(false);
          localStorage.removeItem('discord_token');
          localStorage.removeItem('discord_code_verifier');
          localStorage.removeItem('discord_dashboard_data');
        } else {
          localStorage.removeItem('discord_token');
          localStorage.removeItem('discord_code_verifier');
          localStorage.removeItem('discord_dashboard_data');
          setIsAuthenticated(false);
          setApprovalStatus(null);
        }
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUserData(null);
    setApprovalStatus(null);
    localStorage.removeItem('discord_token');
    localStorage.removeItem('discord_code_verifier');
    localStorage.removeItem('discord_dashboard_data');
  };

  if (loading) {
    return (
      <div className="app-shell full-screen center">
        <div className="spinner" />
        <p className="muted">Lädt...</p>
      </div>
    );
  }

  if (approvalStatus === 'pending') {
    return (
      <div className="app-shell">
        <div className="hero">
          <div className="hero-badge" style={{ background: '#ffc107', color: '#000' }}>Warte auf Bestätigung</div>
          <h1 className="hero-title">Zugang ausstehend</h1>
          <p className="hero-subtitle" style={{ color: '#ffc107' }}>
            Ihr Zugang muss erst von einem Panel Admin bestätigt werden.
          </p>
          <p className="muted" style={{ marginTop: '20px' }}>
            Bitte wenden Sie sich an einen Administrator, um Ihren Zugang freizuschalten.
          </p>
          <button onClick={handleLogout} className="btn btn-outline" style={{ marginTop: '20px', borderColor: '#666', color: '#888' }}>
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  if (approvalStatus === 'rejected') {
    return (
      <div className="app-shell">
        <div className="hero">
          <div className="hero-badge" style={{ background: '#dc3545', color: '#fff' }}>Zugang abgelehnt</div>
          <h1 className="hero-title">Zugriff verweigert</h1>
          <p className="hero-subtitle" style={{ color: '#dc3545' }}>
            Ihr Zugang wurde abgelehnt.
          </p>
          <p className="muted" style={{ marginTop: '20px' }}>
            Bitte kontaktieren Sie einen Administrator, falls Sie Fragen haben.
          </p>
          <button onClick={handleLogout} className="btn btn-outline" style={{ marginTop: '20px', borderColor: '#666', color: '#888' }}>
            Abmelden
          </button>
        </div>
      </div>
    );
  }

  const CREATOR_PLUS_ROLE_ID = '1492514375905316940';
  const hasCreatorPlus = Array.isArray(userData?.roles) && userData.roles.includes(CREATOR_PLUS_ROLE_ID);
  const REDEEM_HISTORY_ROLE_ID = '1496560391071862795';
  const hasRedeemHistoryAccess = Array.isArray(userData?.roles) && userData.roles.includes(REDEEM_HISTORY_ROLE_ID);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<MainLayout userData={userData} handleLogout={handleLogout} isAuthenticated={isAuthenticated} hasRedeemHistoryAccess={hasRedeemHistoryAccess} />}>
          <Route index element={<Dashboard />} />
          <Route path="streams" element={<Streams />} />
          <Route path="top-streamers" element={<TopStreamers />} />
          <Route path="community" element={<Community />} />
          <Route path="faq" element={<FAQ />} />
          <Route path="faq/:slug" element={<FAQDetail />} />
          <Route path="information" element={<Information />} />
          <Route path="streamer-account" element={<StreamerAccount />} />
          <Route path="redeemed-coins" element={<RedeemedCoins />} />
          <Route path="giveaway" element={<Giveaway />} />
          <Route path="twitch-account" element={<TwitchAccount />} />
          <Route path="bot-timers" element={<TwitchTimers />} />
          <Route path="bot-commands" element={<TwitchCommands />} />
          <Route path="chat" element={<ChatPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
